import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-lg border border-cyan-500/20 bg-black/40 p-3" data-testid={testid}>
    <p className="text-[0.65rem] uppercase tracking-widest text-gray-400">{label}</p>
    <p className="text-xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-gray-500">{sub}</p>}
  </div>
);

const statusColor = { match: 'text-green-400', possible: 'text-amber-400', not_electronic: 'text-orange-400', no_match: 'text-red-400' };

// Admin panel: live health + per-scan telemetry for the Track ID engine.
export default function IdEngineDiagnostics() {
  const [stats, setStats] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hunterRun, setHunterRun] = useState(null);

  const postHunters = async (scope) => {
    setHunterRun({ busy: true });
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/id-hunters/post-now?scope=${scope}`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token')}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setHunterRun(d);
    } catch (e) {
      setHunterRun({ error: e.message });
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('beatseek_token');
    const auth = { Authorization: `Bearer ${token}` };
    try {
      const [s, a, h] = await Promise.all([
        fetch(`${BACKEND_URL}/api/track-id/stats`, { headers: auth }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/track-id/attempts?limit=60`, { headers: auth }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/track-id/health`).then(r => r.json()),
      ]);
      if (s.detail) throw new Error(s.detail);
      setStats(s);
      setAttempts(a.attempts || []);
      setHealth(h);
    } catch (e) {
      setError(e.message || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5 p-4" data-testid="id-engine-diagnostics">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Track ID Engine — Diagnostics</h3>
          <p className="text-xs text-gray-400">Every scan is logged with per-stage timings, provider scores and the electronic-gate decision.</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-2 rounded-md border border-cyan-500/40 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-500/10" data-testid="id-diag-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <p className="text-sm text-red-400" data-testid="id-diag-error">{error}</p>}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-pink-500/30 bg-pink-500/5 px-3 py-2 text-xs" data-testid="id-hunters-admin">
        <span className="font-semibold text-pink-300">🎯 Monthly ID Hunters post</span>
        <span className="text-gray-400">auto-runs on the 1st (00:05 UTC) for the previous month</span>
        <button onClick={() => postHunters('previous')} disabled={hunterRun?.busy} className="rounded-md border border-pink-500/40 px-2.5 py-1 text-pink-200 hover:bg-pink-500/10 disabled:opacity-50" data-testid="id-hunters-post-previous">
          Post last month now
        </button>
        <button onClick={() => postHunters('current')} disabled={hunterRun?.busy} className="rounded-md border border-cyan-500/40 px-2.5 py-1 text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-50" data-testid="id-hunters-post-current">
          Post this month's standings
        </button>
        {hunterRun && !hunterRun.busy && (
          <span className={hunterRun.error || !hunterRun.posted ? 'text-amber-300' : 'text-green-300'} data-testid="id-hunters-result">
            {hunterRun.error ? hunterRun.error : hunterRun.posted
              ? `Posted ${hunterRun.month}: ${hunterRun.leaders.map(l => `#${l.rank} @${l.username} (${l.ids})`).join(', ')} · ${hunterRun.pinged} pinged`
              : `Nothing to post for ${hunterRun.month} — no Wall clips were ID'd`}
          </span>
        )}
      </div>

      {health && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="id-diag-health">
          <span className={`rounded-full px-3 py-1 ${health.ffmpeg ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'}`}>ffmpeg {health.ffmpeg ? 'ok' : 'missing'}</span>
          <span className={`rounded-full px-3 py-1 ${health.acrcloud === 'healthy' ? 'bg-green-500/15 text-green-300' : 'bg-amber-500/15 text-amber-300'}`} title={health.acrcloud_error || ''}>ACRCloud {health.acrcloud}{health.acrcloud_error ? ` — ${health.acrcloud_error}` : ''}</span>
          <span className={`rounded-full px-3 py-1 ${health.audd === 'healthy' ? 'bg-green-500/15 text-green-300' : 'bg-amber-500/15 text-amber-300'}`} title={health.audd_error || ''}>AudD {health.audd}{health.audd_paused ? ' (paused)' : ''}</span>
          <span className="rounded-full bg-white/5 px-3 py-1 text-gray-300">dual-submit: {health.dual_submit}</span>
          <span className="rounded-full bg-white/5 px-3 py-1 text-gray-300">budget {health.budget_s}s · thresholds {health.thresholds?.strong}/{health.thresholds?.accept}/{health.thresholds?.possible}</span>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          <Stat label="Scans" value={stats.total} sub={`${stats.window_days}d window`} testid="id-diag-total" />
          <Stat label="Hit rate" value={`${stats.hit_rate_pct}%`} sub={`${stats.by_status?.match || 0} matches`} testid="id-diag-hit-rate" />
          <Stat label="Median time" value={`${(stats.median_total_ms / 1000).toFixed(1)}s`} sub={`p90 ${(stats.p90_total_ms / 1000).toFixed(1)}s`} testid="id-diag-median" />
          <Stat label="Gate blocked" value={stats.by_status?.not_electronic || 0} sub="non-electronic" testid="id-diag-gate" />
          <Stat label="Quick-pass wins" value={`${stats.quick_win_pct}%`} sub="locked at 6s" testid="id-diag-quick" />
          <Stat label="No match" value={stats.by_status?.no_match || 0} sub={Object.entries(stats.by_reason || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'} testid="id-diag-nomatch" />
        </div>
      )}

      {stats?.by_format && Object.keys(stats.by_format).length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-gray-300" data-testid="id-diag-formats">
          {Object.entries(stats.by_format).map(([f, n]) => <span key={f} className="rounded bg-white/5 px-2 py-1">{f}: {n}</span>)}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full text-left text-xs" data-testid="id-diag-table">
          <thead className="bg-white/5 text-gray-400">
            <tr>
              <th className="px-3 py-2">When</th><th className="px-3 py-2">Stage</th><th className="px-3 py-2">Result</th>
              <th className="px-3 py-2">Track</th><th className="px-3 py-2">Conf</th><th className="px-3 py-2">Gate</th>
              <th className="px-3 py-2">Total</th><th className="px-3 py-2">Providers</th><th className="px-3 py-2">Signal</th>
            </tr>
          </thead>
          <tbody>
            {attempts.map(a => {
              const st = a.final_status || a.status;
              return (
                <tr key={a.attempt_id} className="border-t border-white/5 hover:bg-white/5" data-testid="id-diag-row">
                  <td className="whitespace-nowrap px-3 py-2 text-gray-400">{new Date(a.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="px-3 py-2 text-gray-300">{a.stage}</td>
                  <td className={`px-3 py-2 font-semibold ${statusColor[st] || 'text-gray-300'}`}>{st}{a.reason ? <span className="block text-[0.6rem] font-normal text-gray-500">{a.reason}</span> : null}</td>
                  <td className="max-w-[220px] truncate px-3 py-2 text-white">{a.artist ? `${a.artist} – ${a.title}` : '—'}{a.version_type && a.version_type !== 'original' ? <span className="ml-1 text-pink-400">[{a.version_type}]</span> : null}</td>
                  <td className="px-3 py-2 text-gray-300">{a.confidence ? `${Math.round(a.confidence)}%` : '—'}</td>
                  <td className="px-3 py-2 text-gray-300">{a.gate ? `${a.gate.decision}${a.gate.canonical_genre ? ` · ${a.gate.canonical_genre}` : ''}` : '—'}</td>
                  <td className="px-3 py-2 text-gray-300">{a.timings?.total_ms ? `${(a.timings.total_ms / 1000).toFixed(1)}s` : '—'}{a.timings?.gate_ms ? <span className="block text-[0.6rem] text-gray-500">gate {(a.timings.gate_ms / 1000).toFixed(1)}s</span> : null}</td>
                  <td className="px-3 py-2 text-gray-300">{(a.provider_results || []).filter((p, i, arr) => arr.findIndex(q => q.provider === p.provider && q.variant === p.variant) === i).map(p => `${p.provider}/${p.variant} ${Math.round(p.score)}`).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-gray-400">{a.signal?.rms_dbfs != null ? `${a.signal.rms_dbfs} dB` : '—'}{a.client?.format ? <span className="block text-[0.6rem] text-gray-500">{a.client.format.split(';')[0]}</span> : null}</td>
                </tr>
              );
            })}
            {attempts.length === 0 && !loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">No scans logged yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
